import { MAP_SPACE_TYPES } from "./map-components";
import { warp_configs } from "./data/warp_data";
import { ANOMALIES, PLANET_TRAITS, TECH_SPECIALTIES, WORMHOLES } from "./data/tile_data";

export const PLANET_EVAL_STRATEGIES = {
    "SUM": 1,
    "GREATEST": 2,
    "GREATEST_PLUS_TECH": 3,
};

export class Planet {
    constructor(obj_data) {
        this.trait = obj_data.trait;
        this.tech_specialty = obj_data.tech_specialty;
        this.resources = obj_data.resources;
        this.influence = obj_data.influence;
        this.name = obj_data.name;
        this.legendary = obj_data.legendary;
        this.ability = obj_data.ability ? obj_data.ability : "";
    }

    evaluate(variables) {
        let v = variables.data;
        let value = v.BASE_PLANET_MOD;
        let r = this.resources * v.RESOURCES_MULTIPLIER,
            i = this.influence * v.INFLUENCE_MULTIPLIER;
        let t = 0;
        if (this.tech_specialty !== null) {
            t += v.TECH_MOD;
            switch (this.tech_specialty) {
                case TECH_SPECIALTIES.WARFARE:
                    t += v.TECH_WARFARE_MOD;
                    break;
                case TECH_SPECIALTIES.PROPULSION:
                    t += v.TECH_PROPULSION_MOD;
                    break;
                case TECH_SPECIALTIES.BIOTIC:
                    t += v.TECH_BIOTIC_MOD;
                    break;
                case TECH_SPECIALTIES.CYBERNETIC:
                    t += v.TECH_CYBERNETIC_MOD;
                    break;
                default:
                    break;
            }
        }
        switch (v.PLANET_STRATEGY) {
            case PLANET_EVAL_STRATEGIES.GREATEST:
                let to_add = r;
                if (i > to_add) to_add = i;
                if (t > to_add) to_add = t;
                value += to_add;
                break;
            case PLANET_EVAL_STRATEGIES.AVERAGE:
                value += (r + i + t) / 3;
                break;
            default:
                let high_one = r;
                if (i > high_one) high_one = i;
                value += high_one + t;
                break;
        }
        if (r > 0) value += v.NONZERO_RESOURCES_MOD;
        if (i > 0) value += v.NONZERO_INFLUENCE_MOD;
        switch (this.trait) {
            case PLANET_TRAITS.CULTURAL:
                value += v.TRAIT_CULTURAL_MOD;
                break;
            case PLANET_TRAITS.HAZARDOUS:
                value += v.TRAIT_HAZARDOUS_MOD;
                break;
            case PLANET_TRAITS.INDUSTRIAL:
                value += v.TRAIT_INDUSTRIAL_MOD;
                break;
            default:
                break;
        }
        return value;
    }
}

export class PlanetBox {
    constructor(planet_data) {
        this.planets = [];
        for (const one_planet_data of planet_data) {
            this.planets.push(new Planet(one_planet_data));
        }
    }

    getPlanetByName(name) {
        for (const one_planet of this.planets) {
            if (one_planet.name === name) {
                return one_planet;
            }
        }
        return null;
    }
}

export class System {
    constructor(obj_data, planet_box) {
        this.id = obj_data.id;
        if (obj_data.anomaly === null) {
            this.anomaly = []
        } else {
            this.anomaly = obj_data.anomaly
        }
        if (obj_data.wormhole === null) {
            this.wormhole = []
        } else {
            this.wormhole = obj_data.wormhole
        }
        this.planets = [];
        for (const planet_name of obj_data.planets) {
            const plToAdd = planet_box.getPlanetByName(planet_name);
            if (plToAdd) this.planets.push(plToAdd);
        }
    }

    evaluate(variables) {
        let v = variables.data;
        let value = 0;
        for (const one_planet of this.planets) {
            value += one_planet.evaluate(variables);
        }
        if (this.planets.length === 1) {
            value += v.SINGLE_PLANET_MOD;
        } else if (this.planets.length === 2) {
            value += v.MULTI_PLANET_MOD;
            if (this.planets[0].trait === this.planets[1].trait) {
                value += v.MATCHING_PLANETS_MOD;
            } else {
                value += v.NONMATCHING_PLANETS_MOD;
            }
        } else if (this.planets.length > 2) {
            value += 2 * v.MULTI_PLANET_MOD;
            value += v.MATCHING_PLANETS_MOD;
        }
        if (this.isMecatolRexSystem()) {
            value += v.MECATOL_REX_SYS_MOD;
        }
        if (this.isLegendary()) {
            let legendary_mod = v.LEGENDARY_PLANET_SYS_MOD || 0;
            value += legendary_mod;
        }
        return value;
    }

    isLegendary() {
        for (let i = 0; i < this.planets.length; i++) {
            if (this.planets[i].legendary) {
                return true;
            }
        }
        return false;
    }

    getDistanceModifier(variables = {}) {
        let v = variables.data;
        let value = v.DISTANCE_MOD_BASE;
        if (this.isBlue() || this.isMecatolRexSystem()) {
            if (v.DISTANCE_MOD_PLANET === false) return false;
            value += v.DISTANCE_MOD_PLANET;
            if (this.wormhole.length > 0) {
                if (v.DISTANCE_MOD_PLANET_WORMHOLE === false) return false;
                value += v.DISTANCE_MOD_PLANET_WORMHOLE;
            }
        } else if (this.anomaly.length === 0) {
            if (this.wormhole.length > 0) {
                if (v.DISTANCE_MOD_EMPTY_WORMHOLE === false) return false;
                value += v.DISTANCE_MOD_EMPTY_WORMHOLE;
            } else {
                if (v.DISTANCE_MOD_EMPTY === false) return false;
                value += v.DISTANCE_MOD_EMPTY;
            }
        } else if (this.anomaly.length > 0) {
            this.anomaly.forEach(anomaly => {
                switch (anomaly) {
                    case ANOMALIES.ASTEROID_FIELD:
                        if (v.DISTANCE_MOD_ASTEROID_FIELD === false || value === false) value = false;
                        value += v.DISTANCE_MOD_ASTEROID_FIELD;
                        break;
                    case ANOMALIES.GRAVITY_RIFT:
                        if (v.DISTANCE_MOD_GRAVITY_RIFT === false || value === false) return value = false;
                        value += v.DISTANCE_MOD_GRAVITY_RIFT;
                        break;
                    case ANOMALIES.NEBULA:
                        if (v.DISTANCE_MOD_NEBULA === false || value === false) return value = false;
                        value += v.DISTANCE_MOD_NEBULA;
                        break;
                    case ANOMALIES.SUPERNOVA:
                        if (v.DISTANCE_MOD_SUPERNOVA === false || value === false) return value = false;
                        value += v.DISTANCE_MOD_SUPERNOVA;
                        break;
                    default:
                        break;
                }
            }
            )
        }
        return value;
    }

    isMecatolRexSystem() {
        for (const planet of this.planets) {
            if (planet.name === "Mecatol Rex") return true;
        }
        return false;
    }

    isRed() {
        if (this.planets.length < 1 || this.anomaly.length > 0) return true;
        return false;
    }

    isBlue() {
        return !this.isRed() && !this.isMecatolRexSystem();
    }

    getStringName() {
        let system = this;
        let name_array = [];
        for (const planet of system.planets) {
            let tech = "";
            switch (planet.tech_specialty) {
                case TECH_SPECIALTIES.BIOTIC:
                    tech = "/G";
                    break;
                case TECH_SPECIALTIES.WARFARE:
                    tech = "/R";
                    break;
                case TECH_SPECIALTIES.CYBERNETIC:
                    tech = "/Y";
                    break;
                case TECH_SPECIALTIES.PROPULSION:
                    tech = "/B";
                    break;
                default:
                    break;
            }
            name_array.push(
                planet.name + " (" + planet.resources + "/" + planet.influence + tech + ")"
            );
        }
        if (system.wormhole.length > 0) {
            system.wormhole.forEach(wormhole => {
                switch (wormhole) {
                    case WORMHOLES.ALPHA:
                        name_array.push("(α)");
                        break;
                    case WORMHOLES.BETA:
                        name_array.push("(β)");
                        break;
                    case WORMHOLES.GAMMA:
                        name_array.push("(γ)");
                        break;
                    case WORMHOLES.EPSILON:
                        name_array.push("(ε)");
                        break;
                    case WORMHOLES.ZETA:
                        name_array.push("(ζ)");
                        break;
                    case WORMHOLES.ETA:
                        name_array.push("(η)");
                        break;
                    case WORMHOLES.THETA:
                        name_array.push("(θ)");
                        break;
                    case WORMHOLES.IOTA:
                        name_array.push("(ι)");
                        break;
                    case WORMHOLES.KAPPA:
                        name_array.push("(κ)");
                        break;
                    default: break;
                }
            })
        }
        if (system.anomaly.length > 0) {
            system.anomaly.forEach(anomaly => {
                switch (anomaly) {
                    case ANOMALIES.SUPERNOVA:
                        name_array.push("Supernova");
                        break;
                    case ANOMALIES.GRAVITY_RIFT:
                        name_array.push("Gravity Rift");
                        break;
                    case ANOMALIES.NEBULA:
                        name_array.push("Nebula");
                        break;
                    case ANOMALIES.ASTEROID_FIELD:
                        name_array.push("Asteroid Field");
                        break;
                    default:
                        break;
                }
            })
        }
        if (name_array.length === 0) name_array.push("Empty Space");
        return system.id + ": " + name_array.join(", ");
    }
}

export class SystemBox {
    constructor(system_data, planet_box) {
        this.systems = [];
        this.planet_box = planet_box;
        for (const one_system_data of system_data) {
            this.systems.push(new System(one_system_data, planet_box));
        }
    }

    getBlueSystemTotal() {
        let total = 0;
        for (const system of this.systems) {
            if (system.isBlue()) {
                total++;
            }
        }
        return total;
    }

    getRedSystemTotal() {
        let total = 0;
        for (const system of this.systems) {
            if (system.isRed()) {
                total++;
            }
        }
        return total;
    }

    getSystemIndexByID(id) {
        for (const [index, system] of this.systems.entries()) {
            console.log(system.id, id)
            if (system.id === id) return index;
        }
        return null;
    }

    getSystemByID(id, splice = false) {
        let sysindex = this.getSystemIndexByID(id);
        if (sysindex !== null && splice) return this.systems.splice(sysindex, 1);
        if (sysindex !== null) return this.systems[sysindex];
        return null;
    }

    makeCopy() {
        let new_system_box = new SystemBox([], this.planet_box);
        for (const one_system of this.systems) {
            new_system_box.systems.push(one_system);
        }
        return new_system_box;
    }

    getWormholeSystems(type = null) {
        let systems_to_return = [];
        for (const one_sys of this.systems) {
            if (
                one_sys.wormhole.length > 0
                &&
                (
                    type === null
                    ||
                    one_sys.wormhole.includes(type)
                )
            ) {
                systems_to_return.push(one_sys);
            }
        }
        return systems_to_return;
    }
}

export function getDistanceMultiplier(modded_distance, variables) {
    let v = variables.data;
    let d = modded_distance;
    if (d < 0) d = 0;
    if (d > 10) d = 10;
    return v.DISTANCE_MULTIPLIER[d];
}

export class Map {
    constructor(load_default = true, starting_space = null, iterations = 3) {
        if (load_default) {
            this.spaces = [starting_space];
            for (let i = 0; i < iterations; i++) {
                this.addAdjacentSpaces();
            }
        } else {
            this.spaces = [];
        }
    }

    makeCopy() {
        let new_map = new Map(false);
        for (const one_space of this.spaces) {
            let new_space = new MapSpace(
                one_space.x,
                one_space.y,
                one_space.z,
                one_space.warp_spaces,
                one_space.type,
                one_space.system,
            );
            new_map.spaces.push(new_space);
        }
        return new_map;
    }

    containsSystem(system_id) {
        return (this.getSpaceBySystemID(system_id) !== null);
    }

    getSpaceBySystemID(id) {
        for (const one_space of this.spaces) {
            if (
                one_space.system
                && one_space.system.id === id
            ) return one_space;
        }
        return null;
    }

    findSpaceDistance(space_a, space_b, max = 5) {
        let checked_spaces = [space_a];
        let new_to_check_adj = [space_a];
        for (let i = 0; i <= max; i++) {
            if (areCoordsInList(space_b, checked_spaces)) {
                return i;
            }
            let new_new_check = [];
            for (const one_space of new_to_check_adj) {
                let new_adj = this.getAdjacentSpacesIncludingWormholes(one_space);
                for (const one_adj of new_adj) {
                    if (!areCoordsInList(one_adj, checked_spaces)) {
                        checked_spaces.push(one_adj);
                        new_new_check.push(one_adj);
                    }
                }
            }
            new_to_check_adj = new_new_check;
        }
        return max + 1;
    }

    getHomeSystemStats() {
        let stats = [];
        let nonhomes = [];
        for (const space of this.spaces) {
            if (space.type === MAP_SPACE_TYPES.HOME) {
                stats.push({ home: space, slice: [] });
            } else if (space.type === MAP_SPACE_TYPES.SYSTEM && space.system.planets.length > 0) {
                nonhomes.push(space)
            }
        }
        for (const space of nonhomes) {
            let best = 50;
            let winner = null;
            for (let i = 0; i < stats.length; i++) {
                let dis = this.findSpaceDistance(stats[i].home, space);
                if (dis < best) {
                    best = dis;
                    winner = i;
                } else if (dis === best) {
                    winner = null;
                }
            }
            if (winner !== null) {
                stats[winner].slice.push(space);
            }
        }
        let final_stats = [];
        for (const one_stat of stats) {
            let resources = 0;
            let influence = 0;
            let techs = "";
            for (const one_system of one_stat.slice) {
                for (const one_planet of one_system.system.planets) {
                    resources += one_planet.resources;
                    influence += one_planet.influence;
                    switch (one_planet.tech_specialty) {
                        case TECH_SPECIALTIES.WARFARE:
                            techs += "R";
                            break;
                        case TECH_SPECIALTIES.PROPULSION:
                            techs += "B";
                            break;
                        case TECH_SPECIALTIES.BIOTIC:
                            techs += "G";
                            break;
                        case TECH_SPECIALTIES.CYBERNETIC:
                            techs += "Y";
                            break;
                        default:
                            break;
                    }
                }
            }
            techs = techs.split("").sort().join("");
            final_stats.push({
                x: one_stat.home.x,
                y: one_stat.home.y,
                z: one_stat.home.z,
                stats: resources + "/" + influence + " " + techs,
            });
        }
        return final_stats;
    }

    getOpenSpacesTotal() {
        let total = 0;
        for (const space of this.spaces) {
            if (space.type === MAP_SPACE_TYPES.OPEN) {
                total++;
            }
        }
        return total;
    }

    getBlueSystemTotal() {
        let total = 0;
        for (const space of this.spaces) {
            if (space.type === MAP_SPACE_TYPES.SYSTEM && space.system.isBlue()) {
                total++;
            }
        }
        return total;
    }

    getRedSystemTotal() {
        let total = 0;
        for (const space of this.spaces) {
            if (space.type === MAP_SPACE_TYPES.SYSTEM && space.system.isRed()) {
                total++;
            }
        }
        return total;
    }

    getLegendariesTotal() {
        let total = 0;
        for (const space of this.spaces) {
            if (space.type === MAP_SPACE_TYPES.SYSTEM) {
                for (const planet of space.system.planets) {
                    if (planet.legendary) total++
                }
            }
        }
        return total;
    }

    getTotalResources() {
        let total = 0;
        for (const space of this.spaces) {
            if (space.type === MAP_SPACE_TYPES.SYSTEM) {
                for (const planet of space.system.planets) {
                    total += planet.resources;
                }
            }
        }
        return total;
    }
    getTotalInfluence() {
        let total = 0;
        for (const space of this.spaces) {
            if (space.type === MAP_SPACE_TYPES.SYSTEM) {
                for (let planet of space.system.planets) {
                    total += planet.influence;
                }
            }
        }
        return total;
    }
    getTotalTechSpecialties() {
        let total = "";
        for (let space of this.spaces) {
            if (space.type === MAP_SPACE_TYPES.SYSTEM) {
                for (let planet of space.system.planets) {
                    switch (planet.tech_specialty) {
                        case TECH_SPECIALTIES.WARFARE:
                            total += "R";
                            break;
                        case TECH_SPECIALTIES.PROPULSION:
                            total += "B";
                            break;
                        case TECH_SPECIALTIES.BIOTIC:
                            total += "G";
                            break;
                        case TECH_SPECIALTIES.CYBERNETIC:
                            total += "Y";
                            break;
                        default:
                            break;
                    }
                }
            }
        }
        return total.split('').sort().join('');
    }

    getPlanetTraitTotals() {
        let ind = 0;
        let cul = 0;
        let haz = 0;
        for (let space of this.spaces) {
            if (space.type === MAP_SPACE_TYPES.SYSTEM) {
                for (const planet of space.system.planets) {
                    switch (planet.trait) {
                        case PLANET_TRAITS.HAZARDOUS:
                            haz++;
                            break;
                        case PLANET_TRAITS.CULTURAL:
                            cul++;
                            break;
                        case PLANET_TRAITS.INDUSTRIAL:
                            ind++;
                            break;
                        default:
                            break;
                    }
                }
            }
        }
        return haz + "/" + ind + "/" + cul;
    }

    getPossibleSystemTotal() {
        let total = 0;
        for (const space of this.spaces) {
            if (
                space.type === MAP_SPACE_TYPES.SYSTEM
                || space.type === MAP_SPACE_TYPES.OPEN
            ) {
                total++;
            }
        }
        return total;
    }

    areWarpsLogical() {
        for (const space of this.spaces) {
            if (space.type === MAP_SPACE_TYPES.WARP) {
                for (const one_warp of warp_configs[space.warp_spaces]) {
                    for (const one_warp_dir of one_warp) {
                        if (!this.doesWarpDirectionConnect(one_warp_dir, space, [space])) {
                            return false;
                        }
                    }
                }
            }
        }
        return true;
    }

    doesWarpDirectionConnect(warp_direction, space, checked_spaces) {
        let whereItGoes = space.getWarpDirectionCoordinates(warp_direction);
        let spaceItGoes = getObjFromCoord(whereItGoes, this.spaces);
        if (!spaceItGoes) return false;
        if (areCoordsInList(spaceItGoes, checked_spaces)) return false;
        if (spaceItGoes.type === MAP_SPACE_TYPES.CLOSED) return false;
        if (spaceItGoes.type === MAP_SPACE_TYPES.WARP) {
            let oppositeDirection = {
                "x": warp_direction.x * -1,
                "y": warp_direction.y * -1,
                "z": warp_direction.z * -1,
            };
            let directions_to_check = [];
            for (const one_next_warp of warp_configs[spaceItGoes.warp_spaces]) {
                if (areCoordsInList(oppositeDirection, [one_next_warp[0]])) {
                    directions_to_check.push(one_next_warp[1]);
                } else if (areCoordsInList(oppositeDirection, [one_next_warp[1]])) {
                    directions_to_check.push(one_next_warp[0]);
                }
            }
            if (directions_to_check.length === 0) return false;
            checked_spaces.push(spaceItGoes);
            for (const one_direction of directions_to_check) {
                if (!this.doesWarpDirectionConnect(one_direction, spaceItGoes, checked_spaces)) {
                    return false;
                }
            }
        }
        return true;
    }

    isComplete() {
        for (const map_space of this.spaces) {
            if (map_space.type === MAP_SPACE_TYPES.OPEN) {
                return false;
            }
        }
        return true;
    }

    getTotalOpen() {
        return this.spaces.filter(space => space.type === MAP_SPACE_TYPES.OPEN).length
    }

    isLegal() {
        let is_legal = true;
        for (const map_space of this.spaces) {
            if (map_space.type === MAP_SPACE_TYPES.SYSTEM && map_space.system.wormhole.length > 0) {
                for (const one_sys of this.getAdjacentSystems(map_space)) {
                    if (one_sys.type === MAP_SPACE_TYPES.SYSTEM
                        &&
                        // Tests if a wormhole in one system matches one in an adjacent system
                        one_sys.system.wormhole.filter(wormhole => map_space.system.wormhole.includes(wormhole)).length > 0) {
                        is_legal = false;
                        break;
                    }
                }
            }
            if (map_space.type === MAP_SPACE_TYPES.SYSTEM && map_space.system.anomaly.length > 0) {
                for (const one_sys of this.getAdjacentSystems(map_space)) {
                    if (one_sys.type === MAP_SPACE_TYPES.SYSTEM
                        && one_sys.system.anomaly.length > 0) {
                        is_legal = false;
                        break;
                    }
                }
            }
            if (!is_legal) break;
        }
        return is_legal;
    }

    getHomeValue(space, variables) {
        let home_total = 0;
        let spaces_to_get_to = [];
        for (const one_space of this.spaces) {
            if (
                one_space.type === MAP_SPACE_TYPES.SYSTEM &&
                one_space.system.evaluate(variables) > 0 &&
                getCoordsDistance(space, one_space) < 5
            ) {
                spaces_to_get_to.push(one_space);
            }
        }
        for (const one_space of spaces_to_get_to) {
            let shortest_distance = this._getShortestModdedDistance(
                space, one_space, variables
            );
            if (!(shortest_distance === null)) {
                home_total += getDistanceMultiplier(
                    shortest_distance, variables
                ) * one_space.system.evaluate(variables);
            }
        }
        return home_total;
    }

    _getShortestModdedDistance(start_space, dest_space, variables) {
        let finished_paths = [];
        let act_paths = [];

        let first_steps = this.getAdjacentSystemsIncludingWormholes(start_space);
        for (const one_step of first_steps) {
            if (
                one_step.system.getDistanceModifier(variables) !== false
                && this.getMapDistanceModifier(start_space, dest_space, variables) !== false
            ) {
                let new_path = [one_step.system.id];
                if (one_step.system.id === dest_space.system.id) {
                    finished_paths.push(new_path);
                } else {
                    act_paths.push(new_path);
                }
            }
        }
        while (act_paths.length > 0) {
            let new_active_paths = [];
            for (const one_path of act_paths) {
                let results = this._extendPath(start_space, dest_space, one_path, variables);
                for (const one_done of results.finished) {
                    finished_paths.push(one_done);
                }
                for (const one_ongoing of results.ongoing) {
                    new_active_paths.push(one_ongoing);
                }
            }
            act_paths = new_active_paths;
        }
        let first_yet = false;
        let shortest = null;
        for (const one_path of finished_paths) {
            let path_length = this._calculateModdedDistanceFromRaw(one_path, variables, start_space);
            if (!first_yet || path_length < shortest) {
                shortest = path_length;
                first_yet = true;
            }
        }
        return shortest;
    }

    _extendPath(start_space, dest_space, path, variables) {
        let completed_paths = [];
        let ongoing_paths = [];
        let last_step = this.getSpaceBySystemID(path[path.length - 1]);
        let next_steps = this.getAdjacentSystemsIncludingWormholes(last_step);
        for (const one_step of next_steps) {
            if (one_step.system.id === dest_space.system.id) {
                let path_copy = [...path, one_step.system.id];
                completed_paths.push(path_copy);
            } else if (
                !(one_step.system.id in path)
                && one_step.system.getDistanceModifier(variables) !== false
                && this.getMapDistanceModifier(start_space, dest_space, variables) !== false
                && path.length < 4
            ) {
                let path_copy = [...path, one_step.system.id];
                ongoing_paths.push(path_copy);
            }
        }
        return ({
            "finished": completed_paths,
            "ongoing": ongoing_paths,
        });
    }


    _calculateModdedDistanceFromRaw(path, variables, start_space) {
        let modded_dist = 0;
        for (const one_index of path) {
            let one_sys = this.getSpaceBySystemID(one_index);
            modded_dist +=
                one_sys.system.getDistanceModifier(variables)
                + this.getMapDistanceModifier(start_space, one_sys, variables);
        }
        return modded_dist;
    }

    getMapDistanceModifier(start_space, dest_space, variables) {
        let adj_spaces = this.getAdjacentSpacesIncludingWormholes(dest_space);
        for (const one_space of adj_spaces) {
            if (
                one_space.type === MAP_SPACE_TYPES.HOME &&
                !areCoordsInList(start_space, [one_space]
                )
            ) {
                return variables.data.DISTANCE_MOD_ADJACENT_TO_OPPONENT;
            }
        }
        return 0;
    }



    addAdjacentSpaces() {
        let coords_to_add = [];
        for (const map_space of this.spaces) {
            let adjacent_coords = map_space.getAdjacentCoordinates();
            for (const one_adjacent_coords of adjacent_coords) {
                if (
                    !areCoordsInList(one_adjacent_coords, coords_to_add) &&
                    !areCoordsInList(one_adjacent_coords, this.spaces)
                ) {
                    coords_to_add.push(one_adjacent_coords);
                }
            }
        }
        for (const one_coords_to_add of coords_to_add) {
            this.spaces.push(new MapSpace(
                one_coords_to_add.x,
                one_coords_to_add.y,
                one_coords_to_add.z,
            ));
        }
    }

    _getAdjacentSpacesThroughWarps(system_space, warp_space) {
        let adj_systems = [];
        let direction_from = {
            "x": system_space.x - warp_space.x,
            "y": system_space.y - warp_space.y,
            "z": system_space.z - warp_space.z,
        };
        let warps_to_follow = [];
        for (const one_warp of warp_configs[warp_space.warp_spaces]) {
            if (areCoordsInList(direction_from, one_warp)) {
                warps_to_follow.push(one_warp);
            }
        }
        for (const one_warp of warps_to_follow) {
            for (const one_warp_direction of one_warp) {
                if (!areCoordsInList(one_warp_direction, [direction_from])) {
                    let next_space = getObjFromCoord(
                        warp_space.getWarpDirectionCoordinates(one_warp_direction),
                        this.spaces
                    );
                    if (next_space !== null) {
                        if (next_space.type !== MAP_SPACE_TYPES.WARP) {
                            if (!areCoordsInList(next_space, adj_systems)) {
                                adj_systems.push(next_space);
                            }
                        } else {
                            let farther_systems = this._getAdjacentSpacesThroughWarps(warp_space, next_space);
                            for (const one_far_sys of farther_systems) {
                                if (!areCoordsInList(one_far_sys, adj_systems)) {
                                    adj_systems.push(one_far_sys);
                                }
                            }
                        }
                    }
                }
            }
        }
        return adj_systems;
    }

    getAdjacentSystems(space) {
        let adj_spaces = this.getAdjacentSpaces(space);
        let adj_systems = [];
        for (const one_space of adj_spaces) {
            if (one_space.type === MAP_SPACE_TYPES.SYSTEM) {
                adj_systems.push(one_space);
            }
        }
        return adj_systems;
    }

    getAdjacentSpaces(space) {
        let adj_coords = space.getAdjacentCoordinates();
        let adj_systems = [];
        for (const one_coord of adj_coords) {
            let potential_space = getObjFromCoord(one_coord, this.spaces);
            if (potential_space !== null) {
                if (potential_space.type !== MAP_SPACE_TYPES.WARP) {
                    if (!areCoordsInList(potential_space, adj_systems)) {
                        adj_systems.push(potential_space);
                    }
                } else {
                    let potential_systems = this._getAdjacentSpacesThroughWarps(space, potential_space);
                    for (const one_system_space of potential_systems) {
                        if (!areCoordsInList(one_system_space, adj_systems)) {
                            adj_systems.push(one_system_space);
                        }
                    }
                }
            }
        }
        return adj_systems;
    }

    getAdjacentSystemsIncludingWormholes(space) {
        let adj_spaces = this.getAdjacentSpacesIncludingWormholes(space);
        let adj_systems = [];
        for (const one_space of adj_spaces) {
            if (one_space.type === MAP_SPACE_TYPES.SYSTEM) {
                adj_systems.push(one_space);
            }
        }
        return adj_systems;
    }

    getAdjacentSpacesIncludingWormholes(space) {
        let adj_systems = this.getAdjacentSpaces(space);
        let wh_systems = this.getMatchingWormholeSpaces(space);
        for (const one_whs of wh_systems) {
            if (!areCoordsInList(one_whs, adj_systems)) {
                adj_systems.push(one_whs);
            }
        }
        return adj_systems;
    }

    getMatchingWormholeSpaces(space) {
        let matching_spaces = [];
        if (space.type === MAP_SPACE_TYPES.SYSTEM && space.system.wormhole.length > 0) {
            for (const one_space of this.spaces) {
                if (
                    one_space.type === MAP_SPACE_TYPES.SYSTEM
                    &&
                    // Tests if a wormhole in one system matches one in an adjacent system
                    (one_space.system.wormhole.length === 0 ? [] : one_space.system.wormhole).filter(wormhole => space.system.wormhole.includes(wormhole)).length > 0
                    &&
                    one_space.system.id !== space.system.id
                ) {
                    matching_spaces.push(one_space);
                }
            }
        }
        return matching_spaces;
    }

    getWormholeSpaces(type = null) {
        let spaces_to_return = [];
        for (const one_space of this.spaces) {
            if (
                one_space.type === MAP_SPACE_TYPES.SYSTEM
                &&
                one_space.system.wormhole.length > 0
                &&
                (
                    type === null
                    ||
                    one_space.system.wormhole.includes(type)
                )
            ) {
                spaces_to_return.push(one_space);
            }
        }
        return spaces_to_return;
    }

}

export function getObjFromCoord(coords, list) {
    for (const existing_coords of list) {
        if (
            coords.x === existing_coords.x &&
            coords.y === existing_coords.y &&
            coords.z === existing_coords.z
        ) {
            return existing_coords;
        }
    }
    return null;
}

export function getCoordsDistance(coords1, coords2) {
    return Math.max(
        Math.abs(coords1.x - coords2.x),
        Math.abs(coords1.y - coords2.y),
        Math.abs(coords1.z - coords2.z),
    )
}

export function areCoordsInList(coords, list) {
    if (getObjFromCoord(coords, list)) {
        return true;
    }
    return false;
}

export class MapSpace {
    constructor(x, y, z, warp_spaces = null, type = MAP_SPACE_TYPES.OPEN, system = null) {
        this.x = x;
        this.y = y;
        this.z = z;
        this.warp_spaces = warp_spaces;
        this.type = type;
        this.system = system;
    }

    getAdjacentCoordinates() {
        return [
            { "x": this.x + 1, "y": this.y - 1, "z": this.z },
            { "x": this.x + 1, "y": this.y, "z": this.z - 1 },
            { "x": this.x - 1, "y": this.y + 1, "z": this.z },
            { "x": this.x, "y": this.y + 1, "z": this.z - 1 },
            { "x": this.x - 1, "y": this.y, "z": this.z + 1 },
            { "x": this.x, "y": this.y - 1, "z": this.z + 1 },
        ];
    }

    getWarpDirectionCoordinates(warp_direction) {
        return {
            "x": this.x + warp_direction.x,
            "y": this.y + warp_direction.y,
            "z": this.z + warp_direction.z,
        }
    }
}
